import React from 'react';
import styled, { css } from 'styled-components';
import { Link } from 'gatsby';
import PropTypes from 'prop-types';

import Button from './Button';
import SubscribeForm from './SubscribeForm';
import Social from './Social';
import { T5D, T4D } from './Text';

import { media, colors, animation } from '../styles/constants';

const FooterContainer = styled.footer`
  display: flex;
  flex-direction: column;
  max-width: 97rem;
  position: relative;

  ${T5D} {
    white-space: pre-line;
  }

  @media (min-width: ${media.bigDesktop}) {
    max-width: 105rem;
  }

  ${props =>
    props.renderCircle &&
    css`
      padding-top: 10rem;

      @media (min-width: ${media.tabletPortrait}) {
        padding-top: 15rem;
        padding-bottom: 5rem;
      }

      @media (min-width: ${media.tabletLandscape}) {
        padding-top: 28rem;
      }

      @media (min-width: ${media.desktop}) {
        padding-top: 18rem;
        padding-bottom: 12rem;
      }

      @media (min-width: ${media.bigDesktop}) {
        padding-top: 27rem;
      }
    `}
`;

const VerticalContainer = styled.div`
  margin-top: 3rem;
  display: flex;
  flex-wrap: wrap;

  > :nth-child(3),
  > :last-child {
    padding-bottom: 2rem;
  }

  > :nth-child(3) {
    width: 100%;
  }

  @media (min-width: ${media.tabletLandscape}) {
    margin-top: 2.7rem;

    & > :nth-child(odd) {
      width: 43rem;
    }
  }

  @media (min-width: ${media.desktop}) {
    margin-top: 2.7rem;

    & > :nth-child(odd) {
      width: 46rem;
    }

    @media (min-width: ${media.bigDesktop}) {
      margin-top: 4.7rem;
    }
  }
`;

const CallToAction = styled.div`
  padding-bottom: 4rem;
  max-width: 46rem;

  ${Button} {
    margin-top: 1.5rem;
  }

  @media (min-width: ${media.tabletPortrait}) {
    &:first-child {
      padding-right: 5rem;
    }

    max-width: 46rem;

    ${Button} {
      margin-top: 1.8rem;
    }
  }

  @media (min-width: ${media.desktop}) {
    padding-bottom: 5.5rem;
  }

  @media (min-width: ${media.bigDesktop}) {
    padding-bottom: 10rem;

    &:first-child {
      padding-right: 10rem;
    }
  }
`;

const SocialContainer = styled.div`
  margin-top: 2.5rem;
  margin-bottom: 2rem;

  @media (min-width: ${media.tabletLandscape}) {
    margin-top: 0;
    margin-left: 5rem;
  }

  @media (min-width: ${media.bigDesktop}) {
    margin-left: 10rem;
  }
`;

const Footer = React.memo(({ isInView, renderCircle = true }) => (
  <>
    <FooterContainer
      renderCircle={renderCircle}
      {...renderCircle && {
        style: {
          willChange: 'transform, opacity',
          transitionProperty: 'transform, opacity',
          transitionDuration: `${animation.footerCircle}ms`,
          transitionTimingFunction: 'ease-in-out',
          transform: `translateY(${isInView ? 0 : '5rem'})`,
          opacity: isInView ? 1 : 0
        }
      }}
    >
      <T5D as="h2" color={colors.white}>
        {`You Seem Nice.
      Let's Talk.`}
      </T5D>
      <VerticalContainer>
        <CallToAction>
          <T4D color={colors.white}>{"Let's build something."}</T4D>
          <a href="mailto:hello@smallplanet.com">
            <Button type="secondary">New Project</Button>
          </a>
        </CallToAction>
        <CallToAction>
          <T4D color={colors.white}>Want to work here?</T4D>
          <Link to="/contact">
            <Button type="secondary">Join Us</Button>
          </Link>
        </CallToAction>
        <SubscribeForm />
        <SocialContainer>
          <Social />
        </SocialContainer>
      </VerticalContainer>
    </FooterContainer>
  </>
));

Footer.displayName = 'Footer';
Footer.propTypes = {
  isInView: PropTypes.bool.isRequired,
  renderCircle: PropTypes.bool
};

export default Footer;
